import type {
	CoreApiError,
	CoreApiModel,
	CoreApiPaginatedExtendedResponse,
	CoreApiPaginatedResponse,
	CoreApiUuidModel,
	PaginatedParams,
	ParamsType,
} from '@lqa-frontend/api-services';
import type { UseQueryOptions } from '@tanstack/react-query';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { coreApiClient, coreApiQueryKey } from '@lqa-frontend/api-services';
import { useQuery } from '@tanstack/react-query';

export function useCollectionQuery<T>(
	endpoint: string,
	params?: ParamsType,
	options?: CollectionQueryOptions<T>
) {
	return useQuery<AxiosResponse<T[]>, AxiosError<CoreApiError>, T[]>({
		queryKey: coreApiQueryKey(endpoint, 'get', params),
		queryFn: async ({ signal }) =>
			coreApiClient.get<T[]>(endpoint, { params, signal }),
		...options,
		select: (response) => response.data,
	});
}

export type CollectionQueryOptions<T> = Omit<
	UseQueryOptions<AxiosResponse<T[]>, AxiosError<CoreApiError>, T[]>,
	'queryKey' | 'queryFn'
>;

export function usePaginatedQuery<
	T extends CoreApiModel,
	TData extends CoreApiPaginatedResponse<T> = CoreApiPaginatedResponse<T>,
	TParams extends Omit<PaginatedParams, 'sort'> = PaginatedParams,
>(
	endpoint: string,
	params?: TParams,
	options?: PaginatedQueryOptions<T, TData>
) {
	return useQuery<AxiosResponse<TData>, AxiosError<CoreApiError>, TData>({
		queryKey: coreApiQueryKey(endpoint, 'get', params),
		queryFn: async ({ signal }) =>
			coreApiClient.get<TData>(endpoint, {
				params,
				signal,
			} as AxiosRequestConfig),
		...options,
		select: (response) => response.data,
	});
}

export type PaginatedQueryOptions<
	T extends CoreApiModel | CoreApiUuidModel,
	TData extends
		CoreApiPaginatedResponse<T> | CoreApiPaginatedExtendedResponse<T> =
		CoreApiPaginatedResponse<T> | CoreApiPaginatedExtendedResponse<T>,
> = Omit<
	UseQueryOptions<AxiosResponse<TData>, AxiosError<CoreApiError>, TData>,
	'queryKey' | 'queryFn'
>;

export function useDetailQuery<T>(
	itemEndpointFn: (id: string | number) => string,
	id: string | number | undefined,
	options?: Omit<DetailQueryOptions<T>, 'queryKey' | 'queryFn'>,
	params?: ParamsType
) {
	const keyParams =
		params && typeof params === 'object' && !Array.isArray(params)
			? { id, ...(params as Record<string, unknown>) }
			: { id, params };

	const queryKey = params
		? coreApiQueryKey(itemEndpointFn(''), 'get', keyParams)
		: coreApiQueryKey(itemEndpointFn(''), 'get', id);

	return useQuery<AxiosResponse<T>, AxiosError<CoreApiError>, T>({
		queryKey,
		queryFn: async ({ signal }) =>
			coreApiClient.get<T>(itemEndpointFn(id!), {
				...(params ? { params } : {}),
				signal,
			}),
		enabled: !!id,
		...options,
		select: (response) => response.data,
	});
}

export type DetailQueryOptions<T> = UseQueryOptions<
	AxiosResponse<T>,
	AxiosError<CoreApiError>,
	T
>;
